import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { Network, type ConnectionStatus } from '@capacitor/network';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * Single-call Capacitor bridge for the LessAlone shell.
 *
 * Everything in here no-ops on a normal browser — `Capacitor.isNativePlatform()`
 * is the gate. The SPA is the same React build for web and native; the bridge
 * only adds native-only behaviours (status bar, splash, deep links, hardware
 * back, app-state listeners) when the WebView is actually wrapped in Capacitor.
 *
 * Call `initCapacitorBridge` once from the App root effect. It returns a
 * cleanup function that detaches every listener — exposed so a hot reload
 * doesn't double-register handlers and so unit tests can tear down.
 */

/**
 * Defensive native-shell detection.
 *
 * Several signals — in order of reliability:
 *   1. Capacitor.isNativePlatform() — the canonical API, returns true
 *      iff the JS runtime is running inside an iOS or Android shell.
 *   2. Capacitor.getPlatform() === 'ios' | 'android' — older Capacitor
 *      builds sometimes had isNativePlatform() return undefined while
 *      this still worked.
 *   3. window.Capacitor.isNative — the v1/v2 field that some plugins
 *      still seed even on v8.
 *
 * Module-level `const USE_OTP = isNative()` previously evaluated to
 * false on real Android devices when the Capacitor bridge hadn't
 * finished initializing at module-load time, which sent the user
 * down the magic-link web path instead of the OTP code path —
 * breaking sign-in inside the APK. Three signals are belt + braces.
 */
export function isNative(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (Capacitor.isNativePlatform?.()) return true;
    const platform = Capacitor.getPlatform?.();
    if (platform === 'ios' || platform === 'android') return true;
  } catch {
    // Capacitor module not present (web bundle loaded outside any shell).
  }
  const cap = (window as unknown as { Capacitor?: { isNative?: boolean; platform?: string } })
    .Capacitor;
  if (cap?.isNative === true) return true;
  if (cap?.platform === 'ios' || cap?.platform === 'android') return true;
  return false;
}

export type BridgeNavigate = (path: string) => void;

export interface CapacitorBridgeOptions {
  /** React Router push — used by deep-link handler + back button. */
  navigate: BridgeNavigate;
  /**
   * Fires when the OS brings the app back to the foreground. The shell
   * uses this to re-poll the conversation list so a returning user
   * sees fresh state instead of stale data from before backgrounding.
   */
  onResume?: () => void;
  /**
   * Network status updates (online/offline). Drives the offline banner
   * in the AppLayout. Receives the *current* status on subscribe + every
   * change after.
   */
  onNetworkChange?: (status: 'online' | 'offline') => void;
}

/**
 * Install the bridge. Returns a teardown to call on unmount.
 *
 * Listeners installed:
 *  - App.appStateChange — onResume on foreground
 *  - App.appUrlOpen     — deep-link → router navigation
 *  - App.backButton     — Android hardware back → router.back / app.exit
 *  - Keyboard.* events  — toggles a `body.kb-open` class so CSS rules
 *    that adjust the composer can react
 *  - Network.networkStatusChange — push online/offline upstream
 */
export async function initCapacitorBridge(opts: CapacitorBridgeOptions): Promise<() => void> {
  // Browser path: nothing to install.
  if (!isNative()) return () => undefined;

  const detachers: Array<() => void> = [];

  // --- Status bar: edge-to-edge, ink glyphs on the cream paper ---
  try {
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#f7f4ee' });
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch {
    // Best-effort — some Android OEMs reject setBackgroundColor in
    // edge-to-edge mode; the safe-area CSS handles the fallback.
  }

  // --- Splash: kill it now that the SPA shell is mounted ---
  void SplashScreen.hide({ fadeOutDuration: 240 }).catch(() => undefined);

  // --- App resume / pause ---
  const appStateHandle = await App.addListener('appStateChange', (state) => {
    if (state.isActive) opts.onResume?.();
  });
  detachers.push(() => void appStateHandle.remove());

  // --- Deep links (magic-link tap from email / share sheet) ---
  // The URL arrives as e.g. https://lessalone.app/auth/magic?token=abc ;
  // we strip the origin and hand the path+search to React Router so the
  // SPA renders the right route without a full reload.
  const urlHandle = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    try {
      const url = new URL(event.url);
      const target = `${url.pathname}${url.search}${url.hash}`;
      if (target && target !== '/') opts.navigate(target);
    } catch {
      // Malformed URL — ignore.
    }
  });
  detachers.push(() => void urlHandle.remove());

  // --- Android hardware back ---
  // canGoBack reports the WebView history depth. If we can pop, do so;
  // otherwise let the OS exit the app (so the user doesn't get stuck
  // on a screen that has no in-app back affordance).
  const backHandle = await App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      void App.exitApp().catch(() => undefined);
    }
  });
  detachers.push(() => void backHandle.remove());

  // --- Keyboard: expose a body class so CSS can adjust the composer ---
  const kbShow = await Keyboard.addListener('keyboardWillShow', () => {
    document.body.classList.add('kb-open');
  });
  const kbHide = await Keyboard.addListener('keyboardWillHide', () => {
    document.body.classList.remove('kb-open');
  });
  detachers.push(
    () => void kbShow.remove(),
    () => void kbHide.remove(),
  );

  // --- Network status ---
  const reportNetwork = (s: ConnectionStatus) => {
    opts.onNetworkChange?.(s.connected ? 'online' : 'offline');
  };
  const netHandle = await Network.addListener('networkStatusChange', reportNetwork);
  try {
    reportNetwork(await Network.getStatus());
  } catch {
    // ignore
  }
  detachers.push(() => void netHandle.remove());

  return () => {
    for (const detach of detachers) {
      try {
        detach();
      } catch {
        // ignore
      }
    }
  };
}

/**
 * Save or share a small UTF-8 text file (e.g. a markdown doc).
 *
 * On a normal browser this triggers a blob download. Inside the Capacitor
 * shell `<a download>` doesn't surface a save dialog, so we instead write the
 * file to the app's cache directory and open the OS share sheet — the user can
 * then save it to Files / Drive, email it, etc. Our docs are a few KB, so a
 * plain UTF-8 writeFile is fine (the base64/OOM caveat only bites large
 * binaries). Returns what happened so the caller can show a hint.
 */
export async function saveOrShareText(
  filename: string,
  text: string,
  mimeType = 'text/markdown',
): Promise<'shared' | 'downloaded' | 'failed'> {
  if (isNative()) {
    try {
      const written = await Filesystem.writeFile({
        path: filename,
        data: text,
        directory: Directory.Cache,
        encoding: Encoding.UTF8,
      });
      await Share.share({
        title: filename,
        text: `LessAlone — ${filename}`,
        url: written.uri,
        dialogTitle: 'Save or share',
      });
      return 'shared';
    } catch {
      // The user may have dismissed the sheet, or file-share is unavailable —
      // fall back to sharing the text directly, then give up quietly.
      try {
        await Share.share({ title: filename, text });
        return 'shared';
      } catch {
        return 'failed';
      }
    }
  }
  try {
    const blob = new Blob([text], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return 'downloaded';
  } catch {
    return 'failed';
  }
}

/**
 * Light haptic tap. Use sparingly — only on confirmed primary actions
 * (send, ack, reaction). Over-tapping makes the app feel cheap.
 *
 * No-ops on web. Throws are swallowed.
 */
export async function hapticTap(style: 'light' | 'medium' = 'light'): Promise<void> {
  if (!isNative()) return;
  try {
    await Haptics.impact({
      style: style === 'medium' ? ImpactStyle.Medium : ImpactStyle.Light,
    });
  } catch {
    // ignore
  }
}
